import React, { useState } from 'react';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { app } from '../firebase/authConfig';
import { REGION_FULL } from '../constants';

const P = {
  red:'#C62828', redD:'#B71C1C', redL:'#FFEBEE', redB:'#FFCDD2',
  cream:'#FFFAF5', beige:'#F1E6D8', beigeD:'#EDE4D8', beigeB:'#DDD0C0',
  white:'#FFFFFF', border:'#EAE3DA',
  text:'#1A0E06', sub:'#4A3728', muted:'#6B4F3A',
  font:"'DM Sans', 'Segoe UI', sans-serif",
  fontSerif:"'DM Serif Display', Georgia, serif",
  green:'#1B5E20', greenL:'#E8F5E9', greenB:'#A5D6A7',
};

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const navigate = useNavigate();
  const auth = getAuth(app);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    setError('');

    try {
      auth.languageCode = 'fil';
      await sendPasswordResetEmail(auth, email.trim(), {
        url: `${window.location.origin}/reset-password`,
        handleCodeInApp: false,
      });
      setSent(true);
    } catch (err) {
      if (err.code === 'auth/user-not-found' || err.code === 'auth/invalid-email') {
        // Don't reveal whether the email exists — always show success to prevent enumeration
        setSent(true);
      } else if (err.code === 'auth/too-many-requests') {
        setError('Masyadong maraming kahilingan. Subukan muli mamaya.');
      } else if (err.code === 'auth/network-request-failed') {
        setError('Hindi makakonekta. Suriin ang internet connection.');
      } else {
        setError('May error. Subukang muli.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      fontFamily: P.font,
      position: 'relative',
      overflow: 'hidden',
      background: P.beige,
    }}>

      {/* Background */}
      <div style={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        backgroundImage: `linear-gradient(135deg, ${P.redD} 0%, #8B1A1A 40%, #3D0A0A 100%)`,
      }}>
        <svg style={{ position: 'absolute', bottom: 0, left: 0, right: 0, width: '100%', opacity: 0.12 }} viewBox="0 0 1440 320" preserveAspectRatio="none">
          <path fill="#fff" d="M0,160L48,144C96,128,192,96,288,106.7C384,117,480,171,576,181.3C672,192,768,160,864,138.7C960,117,1056,107,1152,117.3C1248,128,1344,160,1392,176L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"/>
        </svg>
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 39px, rgba(255,255,255,0.04) 40px),
                            repeating-linear-gradient(90deg, transparent, transparent 39px, rgba(255,255,255,0.04) 40px)`,
        }}/>
      </div>

      {/* Content */}
      <div style={{
        position: 'relative', zIndex: 1,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 24px',
      }}>

        {/* Brand header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 38, fontWeight: 900, color: '#fff', letterSpacing: '0.1em', lineHeight: 1 }}>
            F.L.A.R.E.
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 8, fontWeight: 500, letterSpacing: '0.04em' }}>
            Flood Level Alert and Risk Evaluation System
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 4, fontWeight: 400 }}>
            {REGION_FULL}
          </div>
        </div>

        {/* Card */}
        <div style={{
          width: '100%',
          maxWidth: 420,
          background: P.white,
          boxShadow: '0 32px 80px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.1)',
          overflow: 'hidden',
          animation: 'slideUp 0.4s cubic-bezier(0.34,1.1,0.64,1)',
        }}>

          {/* Card header */}
          <div style={{
            background: P.red,
            padding: '18px 28px',
            borderBottom: `3px solid ${P.redD}`,
          }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#fff', lineHeight: 1 }}>I-reset ang Password</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.72)', marginTop: 3, fontWeight: 500 }}>
              Ilagay ang iyong email para makatanggap ng reset link
            </div>
          </div>

          {/* Card body */}
          <div style={{ padding: '32px 28px 28px' }}>

            {sent ? (
              <div>
                <div style={{
                  background: P.greenL,
                  border: `1px solid ${P.greenB}`,
                  padding: '16px 14px',
                  marginBottom: 24,
                }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: P.green, marginBottom: 4 }}>
                    Naipadala na ang reset link!
                  </div>
                  <div style={{ fontSize: 12, color: P.sub, lineHeight: 1.6 }}>
                    Kung ang email na iyon ay rehistrado, makatanggap ka ng link para i-reset ang password. Suriin ang iyong inbox (at spam folder).
                  </div>
                </div>
                <button
                  onClick={() => navigate('/login')}
                  style={{
                    width: '100%',
                    background: P.red,
                    color: '#fff', border: 'none',
                    padding: '13px 18px', fontSize: 13,
                    fontWeight: 800, cursor: 'pointer',
                    transition: 'background 0.15s',
                    letterSpacing: '0.04em', fontFamily: P.font,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = P.redD; }}
                  onMouseLeave={e => { e.currentTarget.style.background = P.red; }}
                >
                  Bumalik sa Login
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <label style={{
                    fontSize: 10, fontWeight: 800, color: P.sub,
                    textTransform: 'uppercase', letterSpacing: '0.08em',
                    display: 'block', marginBottom: 7,
                  }}>EMAIL</label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => { setEmail(e.target.value); setError(''); }}
                    placeholder="opisyal@barangay.gov.ph"
                    autoComplete="email"
                    required
                    style={{
                      width: '100%', padding: '12px 14px',
                      border: `1.5px solid ${P.beigeB}`,
                      fontSize: 13, color: P.text, outline: 'none',
                      background: P.beige, boxSizing: 'border-box',
                      transition: 'border-color 0.15s, box-shadow 0.15s',
                      fontFamily: P.font,
                    }}
                    onFocus={e => { e.target.style.borderColor = P.red; e.target.style.boxShadow = `0 0 0 3px ${P.redL}`; }}
                    onBlur={e => { e.target.style.borderColor = P.beigeB; e.target.style.boxShadow = 'none'; }}
                  />
                </div>

                {error && (
                  <div style={{
                    background: P.redL, border: `1px solid ${P.redB}`,
                    padding: '11px 14px', fontSize: 12,
                    color: P.redD, fontWeight: 600,
                  }}>
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    background: loading ? P.muted : P.red,
                    color: '#fff', border: 'none',
                    padding: '13px 18px', fontSize: 13,
                    fontWeight: 800, cursor: loading ? 'not-allowed' : 'pointer',
                    marginTop: 4, transition: 'background 0.15s',
                    letterSpacing: '0.04em', fontFamily: P.font,
                  }}
                  onMouseEnter={e => { if (!loading) e.currentTarget.style.background = P.redD; }}
                  onMouseLeave={e => { if (!loading) e.currentTarget.style.background = loading ? P.muted : P.red; }}
                >
                  {loading ? 'Nagpapadala...' : 'Magpadala ng Reset Link'}
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  style={{
                    background: 'none', border: 'none',
                    color: P.muted, fontSize: 12,
                    cursor: 'pointer', padding: '4px',
                    fontFamily: P.font, fontWeight: 600,
                    textDecoration: 'underline',
                    textDecorationColor: 'transparent',
                    transition: 'color 0.15s, text-decoration-color 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.color = P.red; e.currentTarget.style.textDecorationColor = P.red; }}
                  onMouseLeave={e => { e.currentTarget.style.color = P.muted; e.currentTarget.style.textDecorationColor = 'transparent'; }}
                >
                  Bumalik sa Login
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ marginTop: 28, textAlign: 'center' }}>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', lineHeight: 1.8 }}>
            F.L.A.R.E. · Flood Level Alert and Risk Evaluation System<br />
            {REGION_FULL} · {new Date().getFullYear()}
          </div>
        </div>

      </div>

      <style>{`
        * { box-sizing: border-box; }
        body { font-family: 'DM Sans', 'Segoe UI', sans-serif; -webkit-font-smoothing: antialiased; margin: 0; }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(28px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        input, button, textarea, select { font-family: 'DM Sans', 'Segoe UI', sans-serif; }
      `}</style>
    </div>
  );
}
